/* eslint-disable @eslint-react/dom/no-missing-iframe-sandbox */
import { useVideoPlayer, useIsVisible } from '@folklore/hooks';
import { useVideoTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { useEffect, useImperativeHandle, useState } from 'react';
import { useLocation } from 'wouter';

import addQueryStringToUrl from '../../lib/addQueryStringToUrl';
import getVideoSources from '../../lib/getVideoSources';

import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { useIsIdle } from '../../contexts/IdleContext';
import { useIsCapture } from '../../contexts/LayoutContext';
import { useRequestBase } from '../../contexts/RequestContext';
import Controls from './Controls';

import styles from '../../styles/partials/video.module.css';

const step = 0.01;
const start = 0;
const end = 1;
const progressSteps = Array.from(
    { length: Math.ceil((end - start) / step) },
    (_, i) => start + (i + 1) * step,
);

export interface VideoPlayer {
    buffering: boolean;
}

export interface VideoProps {
    media: Video;
    width?: number | string | null;
    height?: number | string | null;
    autoPlay?: boolean;
    playing?: boolean;
    muted?: boolean;
    loading?: null | 'lazy';
    withoutSize?: boolean;
    cover?: boolean;
    controls?: boolean;
    controlsCompact?: boolean;
    controlsOver?: boolean;
    controlsTheme?: string | null;
    nativeControls?: boolean;
    withoutBlock?: boolean | null;
    playerRef?: React.RefObject<VideoPlayer | null>;
    onPlay?: () => void;
    onPause?: () => void;
    onEnded?: () => void;
    onMute?: () => void;
    onUnmute?: () => void;
    onBufferStart?: () => void;
    onBufferEnd?: () => void;
    onProgress?: (progress: number) => void;
    onClick?: (playing: boolean) => void;
    onClickInfo?: () => void;
    className?: string | null;
    shapeClassName?: string | null;
    embedClassName?: string | null;
}

function Video({
    media,
    width = null,
    height = null,
    autoPlay = false,
    playing = false,
    muted = false,
    loading = 'lazy',
    withoutSize = false,
    cover = false,
    controls = true,
    controlsCompact = false,
    controlsOver = false,
    controlsTheme = null,
    nativeControls = false,
    withoutBlock = null,
    playerRef = null,
    onPlay = null,
    onPause = null,
    onEnded = null,
    onMute = null,
    onUnmute = null,
    onProgress = null,
    onBufferStart = null,
    onBufferEnd = null,
    onClick = null,
    onClickInfo = null,
    className = null,
    shapeClassName = null,
    embedClassName = null,
}: VideoProps) {
    const [location] = useLocation();
    const isCapture = useIsCapture();
    const isLazyLoading = loading === 'lazy' && !isCapture;
    const { ref: refVisible, visible: isVisible } = useIsVisible({
        rootMargin: '200px',
        persist: true,
        disabled: !isLazyLoading,
    });

    const shouldBeEmbedded = !isLazyLoading || isVisible;

    const { id: mediaId, embed = null, url: mediaUrl = null, sources = null } = media;
    const { provider = null, iframeUrl = null, html = null } = embed || {};
    const iframeTitle =
        html !== null && html.match(/title="[^"]+"/) !== null
            ? html.replace(/^.*title="([^"]+)".*$/, '$1')
            : null;

    const baseUri = useRequestBase();

    let finalIframeUrl = iframeUrl;
    if (provider === 'youtube' && iframeUrl !== null) {
        finalIframeUrl = addQueryStringToUrl(iframeUrl, {
            cc_load_policy: muted ? 1 : 0,
            iv_load_policy: 3,
            playsinline: 1,
            autoplay: 0,
            // autoplay: autoPlay && playing ? 1 : 0,
            enablejsapi: 1,
            rel: 0,
            controls: nativeControls ? 1 : 0,
            origin: baseUri,
            widget_referrer: `${baseUri}${location}`,
        });
    } else if (provider === 'vimeo' && iframeUrl !== null) {
        finalIframeUrl = addQueryStringToUrl(iframeUrl, {
            transparent: 0,
            autoplay: 0,
        });
    }

    // Video player
    const videoPlayer = useVideoPlayer({
        service: provider,
        url: shouldBeEmbedded ? iframeUrl || mediaUrl : null,
        embedPlayerId: 'xw15e',
        // autoplay: false,
        autoplay: autoPlay,
        controls: nativeControls,
        // muted: false,
        muted,
        width,
        height,
        onPlay,
        onPause,
        onEnd: onEnded,
        onBufferStart,
        onBufferEnd,
    });

    const {
        ref: refEmbed = null,
        playing: playerPlaying = false,
        muted: playerMuted = muted,
        currentTime,
        duration,
        play = null,
        pause = null,
        mute = null,
        unmute = null,
        seek = null,
        ready = false,
        loaded = false,
    } = videoPlayer || {};

    useImperativeHandle(playerRef, () => videoPlayer);

    const idle = useIsIdle();

    useVideoTracking(videoPlayer, {
        provider,
        id: mediaId,
        title: iframeTitle,
        url: mediaUrl,
        disabled: idle,
        progressSteps,
        onProgress,
    });

    // A video heard over an episode still playing closes the audio player; a muted autoplay
    // leaves it playing until the video is unmuted
    const audioPlayer = useAudioPlayer();
    const { playing: audioPlaying = false, close: closeAudio = null } = audioPlayer || {};
    useEffect(() => {
        if (playerPlaying && !playerMuted && audioPlaying && closeAudio !== null) {
            closeAudio();
        }
    }, [playerPlaying, playerMuted]);

    const [initialMute, setInitialMute] = useState(false);
    const [initialUnmute, setInitialUnmute] = useState(false);

    useEffect(() => {
        if (playerMuted && onMute !== null) {
            onMute();
        } else if (!playerMuted && onUnmute !== null) {
            onUnmute();
        }
    }, [playerMuted]);

    useEffect(() => {
        if (!ready || !loaded) {
            return;
        }
        if (!initialMute && playing && autoPlay) {
            mute();
            setInitialMute(true);
        }
        if (playing && play !== null) {
            play();
        } else if (!playing && pause !== null) {
            pause();
        }
    }, [ready, loaded, playing, play, pause]);

    const onClickBlock = () => {
        const shouldBeUnmuted = playerMuted && !initialUnmute;
        if (shouldBeUnmuted) {
            unmute();
            setInitialUnmute(true);
        }
        let newPlaying = playerPlaying;
        if (!playerPlaying && play !== null) {
            play();
            newPlaying = true;
        } else if (playerPlaying && !shouldBeUnmuted && pause !== null) {
            pause();
            newPlaying = false;
        }
        if (onClick !== null) {
            onClick(newPlaying);
        }
    };

    const onClickPlay = () => {
        play();
        if (onClick !== null) {
            onClick(true);
        }
    };

    const onClickPause = () => {
        pause();
        if (onClick !== null) {
            onClick(false);
        }
    };

    const onClickMute = () => {
        mute();
    };

    const onClickUnmute = () => {
        unmute();
    };

    const onSeek = (newTime) => {
        seek(newTime);
    };

    // Get the iframe
    let embedElement = null;
    if (finalIframeUrl !== null) {
        embedElement = (
            <iframe
                src={finalIframeUrl}
                ref={refEmbed}
                className={classNames([
                    styles.embed,
                    {
                        [embedClassName]: embedClassName !== null,
                    },
                ])}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Embed"
            />
        );
    } else if (html !== null) {
        embedElement = (
            <div
                className={classNames([
                    styles.embed,
                    {
                        [embedClassName]: embedClassName !== null,
                    },
                ])}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        );
    } else if (mediaUrl !== null) {
        embedElement = (
            <video
                ref={refEmbed}
                className={classNames([
                    styles.embed,
                    {
                        [embedClassName]: embedClassName !== null,
                    },
                ])}
                autoPlay={autoPlay}
                muted={playerMuted}
                playsInline
            >
                {getVideoSources(sources || []).map(({ url, mime }) => (
                    <source key={`source-${url}`} src={`${url}#t=0.001`} type={mime} />
                ))}
            </video>
        );
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.cover]: cover,
                    [styles.controlsOver]: controlsOver,
                    [className]: className !== null,
                },
            ])}
            style={
                !withoutSize
                    ? {
                          width,
                          height,
                      }
                    : null
            }
            ref={refVisible}
        >
            <div
                className={classNames([
                    styles.shape,
                    {
                        [shapeClassName]: shapeClassName !== null,
                    },
                ])}
                style={
                    !withoutSize
                        ? {
                              width,
                              height,
                          }
                        : null
                }
            >
                {!withoutBlock ? (
                    <button type="button" onClick={onClickBlock} className={styles.blockButton} />
                ) : null}
                {shouldBeEmbedded ? embedElement : null}
            </div>
            {controls && !nativeControls ? (
                <Controls
                    playing={playerPlaying}
                    muted={playerMuted}
                    currentTime={currentTime}
                    duration={duration}
                    compact={controlsCompact}
                    theme={controlsTheme}
                    className={classNames([
                        styles.controls,
                        {
                            [styles.compact]: controlsCompact,
                        },
                    ])}
                    onPlay={onClickPlay}
                    onPause={onClickPause}
                    onMute={onClickMute}
                    onUnmute={onClickUnmute}
                    onSeek={onSeek}
                    onClickInfo={onClickInfo}
                />
            ) : null}
        </div>
    );
}

export default Video;
